/**
 * T1 — the product page's bundle box inside the quick-add pop-up (2026-10-23).
 *
 * Enmanuel, 2026-10-22: "customers can't select a specific variant from here". Horizon's quick-add
 * clones the product page's details column into the pop-up, then hides every block except title,
 * price, variant picker and buy buttons (allow-list in snippets/quick-add-modal-styles.liquid). On
 * T1 the character is picked INSIDE the bundle box and the plain STYLE row is offscreen
 * (`titan-picker--offscreen`), so the pop-up showed a title and an Add to cart and nothing to pick.
 *
 * The allow-list now lets the bundle box through. This file does the rest, on the cloned product
 * page BEFORE quick-add.js morphs it into the pop-up:
 *
 *  1. Claims the pop-up's cart form for assets/bundle-cart.js — the same two attributes its
 *     claimForms() writes on the product page — so a quick add is the SAME cart write as the
 *     product page: the tier's picks as parent/child lines plus the unlocked free gifts. Unclaimed,
 *     the form falls through to Horizon's native add and puts ONE variant in the cart.
 *  2. Suffixes the cloned form id and section id. Opened on a product page (recommendations), the
 *     clone would otherwise share both with that page, and bundle-selector.js / offer-group.js
 *     look them up document-wide — the pop-up would drive the page's own button and gift unlocks.
 *  3. Phones: quick-add.js throws the details column away and keeps only the picker and the form,
 *     so the bundle box (and the hidden gift group that bundle-cart reads) ride inside
 *     product-form-component.
 *
 * Scoping for bundle-cart.js and bundle-selector.js comes from the `.shopify-section` wrapper in
 * snippets/quick-add-modal.liquid: both resolve their scope with closest('.shopify-section'), and
 * without it the pop-up's form resolved to `document` and read the FIRST bundle on the page.
 *
 * After the add: bundle-cart dispatches cart:update on `document`, and the quick-add dialog only
 * listens on itself, so it would stay open over the cart drawer. Close it here.
 */
import { isMobileBreakpoint } from '@theme/utilities';

const SUFFIX = '--quick-add';

/**
 * ⚠️ FROZEN WHILE A SPLIT TEST USES THIS POP-UP AS ITS CONTROL. ABConvert test 53647 ("FNAF
 * Collection Page — rebuilt grid vs current", live since 2026-10-19) serves the default collection
 * template as CONTROL, and only that arm opens this pop-up — the rebuilt grid has its own quick add.
 * Fixing it mid-test would improve one arm and spoil the read (Chris, 2026-10-23: "it shouldn't").
 * So on those pages the pop-up stays exactly as it was. Delete this entry when that test ends.
 */
const FROZEN_TEMPLATES = ['collection'];

/** @param {Element} grid - the fresh clone of the product page's [data-product-grid-content] */
function prepare(grid) {
  const bundle = grid.querySelector('bundle-group-component');
  if (!bundle) return;

  const template = /** @type {HTMLElement | null} */ (document.querySelector('main[data-template]'))?.dataset.template;
  if (template && FROZEN_TEMPLATES.includes(template)) {
    // Absent rather than allow-listed: the pre-fix pop-up, title + one-variant Add to cart.
    bundle.remove();
    grid.querySelector('offer-group')?.remove();
    return;
  }

  const form = grid.querySelector('product-form-component form');
  if (form) {
    // ⚠️ Never `form.id`: the form holds an <input name="id">, so `form.id` IS that input.
    const oldId = form.getAttribute('id') || '';
    if (oldId && !oldId.endsWith(SUFFIX)) {
      const newId = oldId + SUFFIX;
      form.setAttribute('id', newId);
      grid.querySelectorAll(`[form="${CSS.escape(oldId)}"]`).forEach((el) => el.setAttribute('form', newId));
      grid
        .querySelectorAll(`[data-product-form-id="${CSS.escape(oldId)}"]`)
        .forEach((el) => el.setAttribute('data-product-form-id', newId));
    }
    form.setAttribute('data-bundle-cart-owned', 'true');
    form.setAttribute('on:submit', '');
  }

  grid.querySelectorAll('bundle-group-component[data-section-id], offer-group[data-section-id]').forEach((el) => {
    const id = /** @type {HTMLElement} */ (el).dataset.sectionId || '';
    if (!id.endsWith(SUFFIX)) /** @type {HTMLElement} */ (el).dataset.sectionId = id + SUFFIX;
  });

  // ⚠️ KEY BOTH ELEMENTS BY PRODUCT. morph() matches by tag + id, so opening a second product
  // morphed the first product's <bundle-group-component> in place: same instance, connectedCallback
  // never re-ran, and it kept the FIRST product's variant catalogue. Measured 2026-10-23: Honored
  // T13's pop-up returned Indestructible variant ids for its picks. A per-product id makes morph
  // swap the element, so each product connects fresh.
  // ⚠️ AND REMOVE THE PREVIOUS PRODUCT'S BOXES FIRST. With only the keys, morph inserted the new
  // box and LEFT the old one behind: Honored's pop-up showed two "Special sale ends soon" boxes,
  // the second one Indestructible's.
  const productId = grid.querySelector('input[name="product-id"]')?.getAttribute('value');
  if (productId) {
    bundle.setAttribute('id', `t1-quick-add-bundle-${productId}`);
    grid.querySelector('offer-group')?.setAttribute('id', `t1-quick-add-offer-${productId}`);
  }
  document
    .getElementById('quick-add-modal-content')
    ?.querySelectorAll('bundle-group-component, offer-group')
    .forEach((el) => {
      if (!productId || !el.id.endsWith(`-${productId}`)) el.remove();
    });

  if (isMobileBreakpoint()) {
    const productForm = grid.querySelector('product-form-component');
    if (productForm) {
      productForm.prepend(bundle);
      const offer = grid.querySelector('offer-group');
      if (offer) productForm.append(offer);
    }
  }
}

customElements.whenDefined('quick-add-component').then(() => {
  const QuickAdd = customElements.get('quick-add-component');
  const original = QuickAdd?.prototype.updateQuickAddModal;
  if (typeof original !== 'function' || original.t1Bundle) return;

  /** @param {Element} productGrid */
  async function updateQuickAddModal(productGrid) {
    if (productGrid) prepare(productGrid);
    const result = await original.call(this, productGrid);
    // A fresh box connects BEFORE morph reaches the button, so morph then writes the server's
    // bare "Add to cart" over the box's "Add to cart — $44.95". Re-announce the selected tier.
    document
      .querySelector('#quick-add-modal-content bundle-group-component [data-bundle-radio]:checked')
      ?.dispatchEvent(new Event('change', { bubbles: true }));
    return result;
  }
  updateQuickAddModal.t1Bundle = true;
  QuickAdd.prototype.updateQuickAddModal = updateQuickAddModal;
});

document.addEventListener('cart:update', (event) => {
  const detail = /** @type {CustomEvent} */ (event).detail;
  if (detail?.sourceId !== 'bundle-cart' || detail?.data?.source !== 'bundle-cart') return;
  const dialog = /** @type {any} */ (document.getElementById('quick-add-dialog'));
  if (dialog?.querySelector('dialog')?.open && typeof dialog.closeDialog === 'function') dialog.closeDialog();
});
